/**
 * Loyalty rules: WHEN <trigger> IF <conditions> THEN <actions>.
 *
 * The engine (API) selects the active rules of a program for a trigger, filters them by conditions,
 * audience and limits, and hands the matching ones to `planAwards`, which turns them into ledger
 * entries and benefit grants. Everything here is pure: same input, same plan.
 */
import type { FactScope } from './conditions';

export const LOYALTY_TRIGGERS = {
  'service.completed': { label: 'Viaje completado', scopes: ['service'] as FactScope[], spend: true },
  'customer.birthday': { label: 'Cumpleaños del cliente', scopes: [] as FactScope[], spend: false },
  'referral.qualified': { label: 'Referido calificado (primer viaje del referido)', scopes: ['referral'] as FactScope[], spend: false },
  'member.enrolled': { label: 'Ingreso al programa', scopes: [] as FactScope[], spend: false },
  'campaign.started': { label: 'Inicio de la campaña (a toda la audiencia)', scopes: [] as FactScope[], spend: false },
} as const;
export type LoyaltyTrigger = keyof typeof LOYALTY_TRIGGERS;
export const LOYALTY_TRIGGER_KEYS = Object.keys(LOYALTY_TRIGGERS) as LoyaltyTrigger[];

export type RuleAction =
  /** base points: multiplied by tier and multiplier actions */
  | { type: 'POINTS_FIXED'; points: number; expiresInDays?: number | null }
  /** base points per spend unit of the trip fare (service.completed only) */
  | { type: 'POINTS_PER_SPEND'; unitMinor: number; points: number; maxPoints?: number | null; expiresInDays?: number | null }
  /** extra points never multiplied (promotional points may expire sooner) */
  | { type: 'BONUS_POINTS'; points: number; expiresInDays?: number | null }
  /** 200 = double base points (service.completed only) */
  | { type: 'MULTIPLIER'; pct: number }
  /** issues a voucher of a benefit (discount, free trip, custom perk) */
  | { type: 'GRANT_BENEFIT'; benefitId: string };
export type RuleActionType = RuleAction['type'];

export const RULE_ACTIONS: Record<RuleActionType, { label: string; spendOnly: boolean }> = {
  POINTS_FIXED: { label: 'Puntos fijos', spendOnly: false },
  POINTS_PER_SPEND: { label: 'Puntos por monto gastado', spendOnly: true },
  BONUS_POINTS: { label: 'Puntos extra (no se multiplican)', spendOnly: false },
  MULTIPLIER: { label: 'Multiplicar los puntos del viaje', spendOnly: true },
  GRANT_BENEFIT: { label: 'Entregar un beneficio', spendOnly: false },
};

export const MAX_POINTS_PER_ACTION = 1_000_000;

/** Ledger type used for points produced by a rule (reporting keeps the historic categories). */
export type LedgerType = 'EARN' | 'BONUS' | 'REFERRAL' | 'BIRTHDAY' | 'FREQUENCY';

export interface RuleForPlan {
  id: string;
  version: number;
  campaignId: string | null;
  priority: number;
  trigger: LoyaltyTrigger;
  template: string | null;
  actions: RuleAction[];
}

export type MultiplierPolicy = 'BEST' | 'STACK';

export interface PlanInput {
  trigger: LoyaltyTrigger;
  rules: RuleForPlan[];
  /** fare of the trip (service.completed) */
  fareMinor?: number;
  /** tier multiplier (100 = none) */
  tierPct: number;
  policy: MultiplierPolicy;
}

export interface PlannedEntry {
  ruleId: string;
  ruleVersion: number;
  campaignId: string | null;
  type: LedgerType;
  kind: 'BASE' | 'MULTIPLIER' | 'BONUS';
  points: number;
  /** null = program default expiry */
  expiresInDays: number | null;
  detail: Record<string, unknown>;
}

export interface PlannedGrant {
  ruleId: string;
  ruleVersion: number;
  campaignId: string | null;
  benefitId: string;
}

export interface AwardPlan {
  entries: PlannedEntry[];
  grants: PlannedGrant[];
  basePoints: number;
  effectivePct: number;
  totalPoints: number;
}

const ledgerTypeFor = (trigger: LoyaltyTrigger, template: string | null, kind: PlannedEntry['kind']): LedgerType => {
  if (trigger === 'customer.birthday') return 'BIRTHDAY';
  if (trigger === 'referral.qualified') return 'REFERRAL';
  if (template === 'FREQUENCY') return 'FREQUENCY';
  if (trigger === 'service.completed' && kind === 'BASE') return 'EARN';
  return 'BONUS';
};

const byPriority = (a: RuleForPlan, b: RuleForPlan) => a.priority - b.priority || a.id.localeCompare(b.id);

/**
 * Turns matching rules into ledger entries.
 *   base      = Σ POINTS_FIXED + Σ floor(fare / unit) × points (capped by maxPoints)
 *   BEST      : effective multiplier = max(tier, rule multipliers)       (they do not add up)
 *   STACK     : effective multiplier = tier + Σ (rule multiplier − 100)  (they add up)
 * Base entries carry the tier multiplier; the extra from rule multipliers is a separate entry
 * attributed to the rule (and campaign) that produced it, so campaign cost stays measurable.
 */
export function planAwards(input: PlanInput): AwardPlan {
  const rules = [...input.rules].sort(byPriority);
  const spend = LOYALTY_TRIGGERS[input.trigger].spend;
  const fare = Math.max(0, Math.floor(input.fareMinor ?? 0));
  const entries: PlannedEntry[] = [];
  const grants: PlannedGrant[] = [];
  const multipliers: Array<{ rule: RuleForPlan; pct: number }> = [];
  const bases: Array<{ rule: RuleForPlan; points: number; expiresInDays: number | null; detail: Record<string, unknown> }> = [];

  for (const rule of rules) {
    let base = 0;
    let expiresInDays: number | null = null;
    const detail: Record<string, unknown> = {};
    for (const a of rule.actions) {
      switch (a.type) {
        case 'POINTS_FIXED':
          base += a.points;
          expiresInDays ??= a.expiresInDays ?? null;
          detail.fixed = ((detail.fixed as number) ?? 0) + a.points;
          break;
        case 'POINTS_PER_SPEND': {
          if (!spend) break;
          let p = Math.floor(fare / a.unitMinor) * a.points;
          if (a.maxPoints) p = Math.min(p, a.maxPoints);
          base += p;
          expiresInDays ??= a.expiresInDays ?? null;
          detail.spend = { fareMinor: fare, unitMinor: a.unitMinor, pointsPerUnit: a.points, points: p };
          break;
        }
        case 'BONUS_POINTS':
          if (a.points > 0)
            entries.push({
              ruleId: rule.id,
              ruleVersion: rule.version,
              campaignId: rule.campaignId,
              type: ledgerTypeFor(input.trigger, rule.template, 'BONUS'),
              kind: 'BONUS',
              points: a.points,
              expiresInDays: a.expiresInDays ?? null,
              detail: { bonus: a.points },
            });
          break;
        case 'MULTIPLIER':
          if (spend && a.pct > 100) multipliers.push({ rule, pct: a.pct });
          break;
        case 'GRANT_BENEFIT':
          grants.push({ ruleId: rule.id, ruleVersion: rule.version, campaignId: rule.campaignId, benefitId: a.benefitId });
          break;
      }
    }
    if (base > 0) bases.push({ rule, points: base, expiresInDays, detail });
  }

  // multipliers only apply to what a trip earns (a birthday gift is the same for every tier)
  const tierPct = spend ? Math.max(100, input.tierPct) : 100;
  const basePoints = bases.reduce((s, b) => s + b.points, 0);
  let tierTotal = 0;
  for (const b of bases) {
    const points = Math.floor((b.points * tierPct) / 100);
    tierTotal += points;
    if (points > 0)
      entries.push({
        ruleId: b.rule.id,
        ruleVersion: b.rule.version,
        campaignId: b.rule.campaignId,
        type: ledgerTypeFor(input.trigger, b.rule.template, 'BASE'),
        kind: 'BASE',
        points,
        expiresInDays: b.expiresInDays,
        detail: { ...b.detail, base: b.points, tierPct },
      });
  }

  let effectivePct = tierPct;
  if (basePoints > 0 && multipliers.length) {
    if (input.policy === 'BEST') {
      // highest multiplier wins; ties go to the higher priority rule (already sorted)
      const best = multipliers.reduce((m, x) => (x.pct > m.pct ? x : m));
      if (best.pct > tierPct) {
        effectivePct = best.pct;
        const extra = Math.floor((basePoints * best.pct) / 100) - tierTotal;
        if (extra > 0) entries.push(multiplierEntry(input.trigger, best.rule, extra, { base: basePoints, pct: best.pct, tierPct, policy: 'BEST' }));
      }
    } else {
      for (const m of multipliers) {
        effectivePct += m.pct - 100;
        const extra = Math.floor((basePoints * (m.pct - 100)) / 100);
        if (extra > 0) entries.push(multiplierEntry(input.trigger, m.rule, extra, { base: basePoints, pct: m.pct, tierPct, policy: 'STACK' }));
      }
    }
  }
  return { entries, grants, basePoints, effectivePct, totalPoints: entries.reduce((s, e) => s + e.points, 0) };
}

function multiplierEntry(trigger: LoyaltyTrigger, rule: RuleForPlan, points: number, detail: Record<string, unknown>): PlannedEntry {
  return {
    ruleId: rule.id,
    ruleVersion: rule.version,
    campaignId: rule.campaignId,
    type: ledgerTypeFor(trigger, rule.template, 'MULTIPLIER'),
    kind: 'MULTIPLIER',
    points,
    expiresInDays: null,
    detail,
  };
}

// ---------------------------------------------------------------------------------------- validation
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const int = (v: unknown, min: number, max: number) => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;

export function validateActions(actions: unknown, trigger: LoyaltyTrigger): string[] {
  const errors: string[] = [];
  if (!Array.isArray(actions) || actions.length === 0) return ['La regla necesita al menos una acción.'];
  if (actions.length > 10) return ['Máximo 10 acciones por regla.'];
  const spend = LOYALTY_TRIGGERS[trigger]?.spend ?? false;
  actions.forEach((a: any, i) => {
    const at = `acción ${i + 1}`;
    const def = a && RULE_ACTIONS[a.type as RuleActionType];
    if (!def) return void errors.push(`${at}: tipo de acción desconocido.`);
    if (def.spendOnly && !spend) return void errors.push(`${at}: "${def.label}" solo aplica a viajes completados.`);
    const exp = a.expiresInDays;
    if (exp !== undefined && exp !== null && !int(exp, 1, 3650)) errors.push(`${at}: el vencimiento debe estar entre 1 y 3650 días.`);
    const allowed: Record<RuleActionType, string[]> = {
      POINTS_FIXED: ['type', 'points', 'expiresInDays'],
      POINTS_PER_SPEND: ['type', 'unitMinor', 'points', 'maxPoints', 'expiresInDays'],
      BONUS_POINTS: ['type', 'points', 'expiresInDays'],
      MULTIPLIER: ['type', 'pct'],
      GRANT_BENEFIT: ['type', 'benefitId'],
    };
    if (Object.keys(a).some((k) => !allowed[a.type as RuleActionType].includes(k))) errors.push(`${at}: contiene campos no permitidos.`);
    switch (a.type as RuleActionType) {
      case 'POINTS_FIXED':
      case 'BONUS_POINTS':
        if (!int(a.points, 1, MAX_POINTS_PER_ACTION)) errors.push(`${at}: los puntos deben estar entre 1 y ${MAX_POINTS_PER_ACTION}.`);
        break;
      case 'POINTS_PER_SPEND':
        if (!int(a.unitMinor, 1, 1e12)) errors.push(`${at}: el monto por punto debe ser mayor que cero.`);
        if (!int(a.points, 1, MAX_POINTS_PER_ACTION)) errors.push(`${at}: los puntos por unidad deben estar entre 1 y ${MAX_POINTS_PER_ACTION}.`);
        if (a.maxPoints !== undefined && a.maxPoints !== null && !int(a.maxPoints, 1, MAX_POINTS_PER_ACTION)) errors.push(`${at}: el tope de puntos no es válido.`);
        break;
      case 'MULTIPLIER':
        if (!int(a.pct, 101, 1000)) errors.push(`${at}: el multiplicador debe estar entre 101% y 1000%.`);
        break;
      case 'GRANT_BENEFIT':
        if (typeof a.benefitId !== 'string' || !UUID.test(a.benefitId)) errors.push(`${at}: elige un beneficio.`);
        break;
    }
  });
  return errors;
}

/** How a customer reads a multiplier: 200 -> "x2", 150 -> "x1,5", 125 -> "x1,25". */
export function multiplierLabel(pct: number): string {
  return `x${String(Math.round(pct) / 100).replace('.', ',')}`;
}

export function benefitIdsOf(actions: RuleAction[]): string[] {
  return actions.filter((a): a is Extract<RuleAction, { type: 'GRANT_BENEFIT' }> => a.type === 'GRANT_BENEFIT').map((a) => a.benefitId);
}
